import React from 'react';
import { Flex } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { useOverrides } from '@shared/utils/overrides';
import { StyledRoot, StyledLeftSection, StyledRightSection, StyledTitle } from './AdminHeaderBar.styles';
import { HEADER_HEIGHT, MOBILE_NAV_ROW_HEIGHT } from './AdminHeaderBar.constants';
// The two modules directly, not the `AdminShell` barrel: every tabbed admin page renders this bar,
// and the barrel also carries the shell's layout and gate, which only flag-on areas load.
import { AdminShellBrand } from '@apps/admin/components/AdminShell/AdminShellBrand';
import { ADMIN_SHELL_TITLE_FONT_WEIGHT, ADMIN_SHELL_TITLE_INSET_X } from '@apps/admin/components/AdminShell/AdminShell.constants';

import { AdminHeaderBarSpacer } from './AdminHeaderBarSpacer';
import { AdminHeaderBarSurface } from './AdminHeaderBarSurface';
import type { AdminHeaderBarProps } from './AdminHeaderBar.types';
import { CommandMenuSearchButton } from '@apps/admin/components/AdminCommandMenu/CommandMenuSearchButton';

/**
 * AdminHeaderBar - the shared admin shell header for every admin product area.
 *
 * Sizes come from `AdminShell.constants` (the website editor's header): a 64px bar at every
 * width and the shared `AdminShellBrand` cluster — 24px hamburger, 34.94×22 XS Joy logo (hidden
 * below `md`), 17px/600 title. Areas supply content through slots, never sizes.
 *
 * Features:
 * - Composable slots for navigation and actions
 * - Fixed, static, or sticky positioning
 * - Mobile tab navigation wraps onto a 48px second row below `md`
 * - Configurable bottom border
 * - Support for native webview (hide hamburger/logo)
 * - Style overrides via useOverrides pattern
 *
 * Anything laid out against the viewport below a fixed header reads `ADMIN_HEADER_HEIGHT`; a
 * fixed header is followed by `<AdminHeaderBar.Spacer />`.
 *
 * @example
 * // Fixed header with spacer (WebsiteOverview)
 * <AdminHeaderBar
 *   title="Website"
 *   navigation={<WebsiteTabNav />}
 *   actions={<HeaderQuickAccessLinks />}
 * />
 * <AdminHeaderBar.Spacer navigation />
 *
 * @example
 * // Static header (Stationery)
 * <AdminHeaderBar
 *   title="Stationery"
 *   position="static"
 *   showBorder
 *   navigation={<CardsTabNav />}
 *   actions={<StationeryHeaderActions />}
 * />
 *
 * @example
 * // Sticky header (Registry)
 * <AdminHeaderBar
 *   title="Registry"
 *   position="sticky"
 *   navigation={<RegistryTabNav />}
 *   actions={<HelpLinks />}
 * />
 */
const AdminHeaderBarComponent: React.FC<AdminHeaderBarProps> = props => {
  const {
    title,
    titleAdornment,
    navigation,
    leadingContent,
    actions,
    mobileNavigation = 'auto',
    position = 'fixed',
    hideHamburger = false,
    nativePadding = false,
    hideLogo = false,
    hideTitle = false,
    hideSearchButton = false,
    showBorder = false,
    onTitleClick,
    overrides = {},
    'aria-label': ariaLabel,
    'data-testid': testId = 'admin-header-bar',
  } = props;

  const showMobileNav = !!navigation && mobileNavigation !== 'desktop-only';
  const hasMainRowContent = !hideHamburger || !hideLogo || !hideTitle || !hideSearchButton || !!actions || !!leadingContent;

  const { tq } = useTranslation('adminHeaderBar');

  const {
    Root: [Root, rootProps],
    LeftSection: [LeftSection, leftSectionProps],
    RightSection: [RightSection, rightSectionProps],
    Title: [Title, titleProps],
  } = useOverrides(
    {
      Root: StyledRoot,
      LeftSection: StyledLeftSection,
      RightSection: StyledRightSection,
      Title: StyledTitle,
    },
    overrides,
  );

  // With mobile tabs the tab row wraps under the main row below `md`, so the root only sets a floor
  // there; otherwise the bar is one fixed height at every width.
  const heightStyles = showMobileNav ? (hasMainRowContent ? { minHeight: { _: HEADER_HEIGHT }, height: { md: HEADER_HEIGHT } } : {}) : { height: HEADER_HEIGHT };

  const handleTitleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onTitleClick?.();
    }
  };

  const titleNode = !hideTitle && (
    <Flex
      alignItems="center"
      gap={6}
    >
      <Title
        // The shared shell title — `AdminShellBrand` and `SectionSwitcherDropdown` use the same
        // hed2 / mono14 at ADMIN_SHELL_TITLE_FONT_WEIGHT, so every admin area's title reads alike.
        typographyVariant="hed2"
        fontWeight={ADMIN_SHELL_TITLE_FONT_WEIGHT}
        color="mono14"
        // Same text inset as the website editor's dropdown title, whose padding holds its hover pill.
        paddingLeft={ADMIN_SHELL_TITLE_INSET_X}
        data-testid="admin-header-bar-title"
        {...(onTitleClick && {
          cursor: 'pointer',
          onClick: onTitleClick,
          // A clickable title is a control: without a role, a tab stop and Enter/Space handling,
          // keyboard and screen-reader users can't reach the action it triggers (e.g. Stationery
          // home). `preventDefault` on Space stops the page scrolling as the button fires.
          role: 'button',
          tabIndex: 0,
          onKeyDown: handleTitleKeyDown,
          // Keyboard-only ring, matching the other non-text controls (2px inputBorderFocused, offset 2px).
          _focusVisible: { outline: '2px solid', outlineColor: 'inputBorderFocused', outlineOffset: '2px' },
        })}
        {...titleProps}
      >
        {title}
      </Title>
      {titleAdornment}
    </Flex>
  );

  const positionStyles =
    position === 'fixed'
      ? {
          position: 'fixed' as const,
          backdropFilter: { _: 'blur(6px)', md: 'blur(12px)' },
        }
      : position === 'sticky'
        ? {
            position: 'sticky' as const,
            top: 0,
          }
        : {};

  return (
    <Root
      as="header"
      role="banner"
      aria-label={ariaLabel ?? (typeof title === 'string' && title ? tq`${title} admin header` : tq`Admin header`)}
      data-testid={testId}
      display="flex"
      flexWrap={showMobileNav ? { _: 'wrap', md: 'nowrap' } : 'nowrap'}
      width="100%"
      alignItems="center"
      justifyContent="space-between"
      backgroundColor="white"
      zIndex="docked"
      paddingLeft={5}
      paddingRight={5}
      {...(nativePadding && { paddingLeft: 6 })}
      {...(showBorder && {
        borderBottom: '1px solid',
        borderColor: 'mono3',
      })}
      {...heightStyles}
      {...positionStyles}
      {...rootProps}
    >
      <LeftSection
        as="div"
        display="flex"
        gap={8}
        alignItems="center"
        order={1}
        data-testid="admin-header-bar-left"
        {...(showMobileNav && hasMainRowContent && { minHeight: { _: HEADER_HEIGHT, md: 'auto' } })}
        {...leftSectionProps}
      >
        {(!hideHamburger || !hideLogo || !hideTitle || !!leadingContent) && (
          <Flex
            alignItems="center"
            zIndex={1}
            columnGap={leadingContent ? 4 : undefined}
          >
            {leadingContent}
            <AdminShellBrand
              logoId="admin-header-joy-logo"
              hideHamburger={hideHamburger}
              hideLogo={hideLogo}
              logoAlwaysVisible={false}
              titleSlot={titleNode || undefined}
            />
          </Flex>
        )}
        {navigation && !showMobileNav && <Flex display={{ _: 'none', md: 'flex' }}>{navigation}</Flex>}
      </LeftSection>
      {showMobileNav && (
        <Flex
          alignItems="center"
          justifyContent={{ _: 'center', md: 'flex-start' }}
          order={{ _: 3, md: 1 }}
          flex={{ _: '0 0 100%', md: '1 1 auto' }}
          minWidth={0}
          height={{ _: pxToRem(MOBILE_NAV_ROW_HEIGHT), md: 'auto' }}
          marginLeft={{ md: 8 }}
          data-testid="admin-header-bar-nav"
        >
          {navigation}
        </Flex>
      )}
      <RightSection
        as="div"
        display="flex"
        alignItems="center"
        flexShrink={1}
        paddingLeft={[4, 6]}
        columnGap={{ _: 0, md: 3 }}
        order={2}
        data-testid="admin-header-bar-right"
        {...rightSectionProps}
      >
        {!hideSearchButton && <CommandMenuSearchButton source="headerBar" />}
        {actions}
      </RightSection>
    </Root>
  );
};

AdminHeaderBarComponent.displayName = 'AdminHeaderBar';

/**
 * AdminHeaderBar with attached sub-components.
 *
 * - `<AdminHeaderBar.Spacer />` — push content below a fixed header.
 * - `<AdminHeaderBar.Surface />` — frosted-glass wrapper for multi-tier
 *   sticky headers (use with `position="static"` on AdminHeaderBar).
 */
export const AdminHeaderBar = Object.assign(AdminHeaderBarComponent, {
  Spacer: AdminHeaderBarSpacer,
  Surface: AdminHeaderBarSurface,
});
